import { Comment, NotificationImportant } from '@mui/icons-material';
import {
  type HeadlineResponse,
  type TrendingListItemResponseTrendingListResponseRelatedPagedData,
} from '@on3/api';
import { On3IconPremiumPlus } from '@on3/icons';
import { Link } from '@on3/ui-lib/components/Link';
import { SocialIcon } from '@on3/ui-lib/components/SocialIcon';
import { useAuth } from '@on3/ui-lib/src/contexts/AuthProvider';
import { useSite } from '@on3/ui-lib/src/contexts/SiteProvider';
import { toFriendlyDate } from '@on3/ui-lib/src/utils/date';
import { cmsUrl, databaseUrl } from '@on3/ui-lib/utils/api';
import { MobileMPU } from '@public/components/Ads/MobileMPU';
import { MPU } from '@public/components/Ads/MPU';
import { VideoPlayer } from '@public/components/Ads/VideoPlayer';
import { PremiumBlock } from '@public/components/Db/Profile/PremiumBlock/PremiumBlock';
import { ArticleScripts } from '@public/components/Headlines/Article/ArticleScripts';
import { EditorSlot } from '@public/components/Headlines/Article/EditorSlot';
import { ShareIcons } from '@public/components/Headlines/Article/ShareIcons/ShareIcons';
import { ResizedImage } from '@public/components/Image/ResizedImage';
import { SignUpModule } from '@public/components/Newsletter/SignUpModule';
import { On3HeaderGrey } from '@public/components/Svg/On3HeaderGrey';
import { TopHeadlines } from '@public/components/TopHeadlines';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import parse, { type HTMLReactParserOptions } from 'html-react-parser';
import Head from 'next/head';
import { usePathname } from 'next/navigation';
import { Fragment, useMemo, useState } from 'react';

import styles from './Article.module.scss';

declare global {
  interface Window {
    incUnits: number;
  }
}

interface IArticleProps extends HeadlineResponse {
  articleNumber: number;
  articleKey?: number | null;
  loaded?: boolean;
  lastArticle: boolean;
  trendingList:
    | TrendingListItemResponseTrendingListResponseRelatedPagedData[]
    | TrendingListItemResponseTrendingListResponseRelatedPagedData
    | null;
}

export const Article = ({
  slug,
  title,
  body,
  featuredImage,
  author,
  postDate,
  fullUrl,
  articleNumber,
  articleKey,
  isPremium,
  primaryCategory,
  loaded,
  hasYouTubeVideo,
  lastArticle,
  trendingList,
  categories,
}: IArticleProps) => {
  const { currentSite } = useSite();
  const primaryColor = currentSite?.linkColor || currentSite?.primaryColor;
  const {
    user,
    isDeveloper,
    hasArticleEditAccess,
    canEditArticle,
    canSlotArticle,
  } = useAuth();

  const isFeed = currentSite?.template === 'Feed';
  const isFanSite = currentSite?.isTeam;
  const isKsr = currentSite?.key === 24;
  const isFirstArticle = articleNumber === 0;
  const hasAccess = useMemo(
    () => !isPremium || (isPremium && (user?.has || hasArticleEditAccess())),
    [isPremium, user?.has, hasArticleEditAccess],
  );
  const pathname = usePathname();
  const showFeaturedImage = (featuredImage && !hasYouTubeVideo) || !hasAccess;

  let totalIncUnits = (typeof window !== 'undefined' && window?.incUnits) || 0;
  const [runningTotal, setRunningTotal] = useState(0);
  const scripts: string[] = [];

  const options: HTMLReactParserOptions = {
    replace(domNode) {
      if ('attribs' in domNode && domNode.type === 'script') {
        if (domNode.attribs['data-ignore']) {
          return null;
        }

        if (!scripts.includes(domNode.attribs.src)) {
          scripts.push(domNode.attribs.src);
        }
      }
    },
  };

  // added replace here. until serg can update content
  const parsedMarkup = parse(
    (body || '').replace(
      /https:\/\/www\.on3\.com\/db\//g,
      'https://www.on3.com/rivals/',
    ),
    options,
  );

  /** Get number of p tags in article to know where to place ads */
  const pTagsCount =
    (Array.isArray(parsedMarkup) &&
      parsedMarkup.filter(
        (item?: JSX.Element) =>
          item?.type === 'p' && item?.props?.children?.toString().length > 100,
      ).length) ||
    0;

  const suggestedContent = pTagsCount > 3 && Math.round(pTagsCount * 0.6);

  /** Function that inserts ad depending on pTagsCount */
  const markupWithAds = useMemo(() => {
    let pTags = 0;
    let runningAds = 0;
    /** First ad will always be placed after 3rd paragraph */
    const adPlacement = [3];

    // these are separated incase we want to adjust individually
    const inArticleVideoAdPlacement: number | null = 2;

    const paragraphSkip = isFanSite ? 4 : 2;
    const offset = isFanSite ? 7 : 5;

    for (let i = offset; i <= pTagsCount; i += paragraphSkip) {
      adPlacement.push(i);
    }

    const newMarkup =
      Array.isArray(parsedMarkup) &&
      parsedMarkup.map((item) => {
        // Some writers have 1 word paragraphs. This was added to prevent ads from showing too often.
        const isEligibleNode =
          item.type === 'p' &&
          (item?.props?.children?.toString().length || 0) >= 100;

        if (isEligibleNode) {
          pTags = pTags + 1;
          let adID = '';
          let mobileAdID = '';
          let showVideoPlayer = false;
          let hasSuggested = false;
          const hasDisplayAd = adPlacement.includes(pTags);

          if (hasDisplayAd) {
            runningAds++;
            // first ad in first article needs to be mobile_mpu_top
            if (isFirstArticle && adPlacement.indexOf(pTags) === 0) {
              adID = 'mpu_inc-1';
              mobileAdID = 'mobile_mpu_top';
            } else {
              // all others will be mobile_mpu_inc-#
              totalIncUnits++;
              adID = `mpu_inc-${totalIncUnits + 1}`;
              mobileAdID = `mobile_mpu_inc-${totalIncUnits}`;
            }
          }

          if (pTags === inArticleVideoAdPlacement && !hasYouTubeVideo) {
            showVideoPlayer = true;
          }

          if (pTags === suggestedContent) {
            hasSuggested = true;
          }

          return (
            <Fragment key={`${slug}-${pTags}`}>
              {item}
              {showVideoPlayer && <VideoPlayer />}
              {hasDisplayAd && (
                <>
                  <MobileMPU id={mobileAdID} incontent={true} />
                  <MPU id={adID} incontent={true} />
                </>
              )}
              {hasSuggested && trendingList && (
                <div className={styles.trending}>
                  <TopHeadlines trendingList={trendingList} />
                  <SignUpModule
                    className={styles.signUp}
                    title="Get the Daily On3 Newsletter in your inbox every morning"
                  />
                </div>
              )}
            </Fragment>
          );
        }

        return item;
      });

    setRunningTotal(runningAds);

    return newMarkup;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showAdminBar =
    isDeveloper() || canEditArticle(currentSite?.key || 0) || canSlotArticle();
  const editorSites = canSlotArticle();
  const editLink = `${cmsUrl}/wp-admin/post.php?post=${articleKey}&action=edit`;
  const notificationLink = `${databaseUrl}/developers/notification-details/${articleKey}/`;

  if (!loaded) {
    return null;
  }

  return (
    <div
      className={styles.articleWrapper}
      data-article
      data-articlekey={articleKey}
      data-totalads={runningTotal}
    >
      {isKsr && (
        <Head>
          <meta content="Kentucky Wildcats" property="og:category" />
        </Head>
      )}
      <style>
        {`
          :root {
            --primary-color: ${primaryColor};
          }
        `}
      </style>

      <ArticleScripts data={scripts} key={pathname} />

      <article
        className={clsx(styles.article, {
          [styles.scrollArticle]: !lastArticle,
        })}
        id={`article-${articleNumber + 1}`}
      >
        {showAdminBar && (
          <div className={styles.adminBlock}>
            {canEditArticle(currentSite?.key || 0) && (
              <a className={styles.editlink} href={editLink}>
                Edit Article
              </a>
            )}
            <EditorSlot
              articleKey={articleKey}
              categories={categories}
              editorSites={editorSites}
            />
            {isDeveloper() && (
              <a className={styles.notificationLink} href={notificationLink}>
                <NotificationImportant />
              </a>
            )}
          </div>
        )}
        <header className={styles.header}>
          {isPremium && hasAccess && (
            <PremiumBlock
              className={styles.premiumBlock}
              title="You are reading premium content."
            />
          )}

          <Text className={styles.heading} variant="h1">
            {title}
          </Text>

          <div className={styles.topauthorblock}>
            {author?.profilePictureResponse && (
              <ResizedImage
                className={styles.avatar}
                {...author?.profilePictureResponse}
                fit="cover"
                gravity="0.5x0.5"
                height={40}
                loading={isFirstArticle ? 'eager' : 'lazy'}
                quality={90}
                width={40}
              />
            )}
            <span>
              by:{' '}
              <Link
                href={`/user/${author?.niceName}`}
                style={{
                  color: `var(--site-link-color, var(--site-primary-color, ${primaryColor}))`,
                }}
              >
                {author?.name || author?.niceName}
              </Link>
            </span>
            <span>{toFriendlyDate(postDate)}</span>
            {author?.twitterHandle && (
              <span className={styles.xBlock}>
                <Link
                  className={styles.xLink}
                  href={`https://x.com/${author?.twitterHandle}`}
                  style={{
                    color: `var(--site-link-color, var(--site-primary-color, ${primaryColor}))`,
                  }}
                >
                  <SocialIcon type="twitter" />
                  <span>{author?.twitterHandle}</span>
                </Link>
              </span>
            )}
            {isPremium && hasArticleEditAccess() && (
              <On3IconPremiumPlus
                className={styles.on3Icon}
                height={14}
                width={40}
              />
            )}
          </div>
          <div className={styles.shareBlock}>
            <ShareIcons
              fullUrl={fullUrl ?? ''}
              primaryCategoryKey={primaryCategory?.key}
              slug={slug}
              title={title ?? ''}
            />
          </div>
        </header>
        <div>
          <div>
            {showFeaturedImage && (
              <figure className={styles.figure}>
                <ResizedImage
                  className={styles.featuredimage}
                  {...featuredImage}
                  fit="cover"
                  gravity="0.5x0.5"
                  height={417}
                  loading={isFirstArticle ? 'eager' : 'lazy'}
                  quality={90}
                  width={795}
                />

                {featuredImage?.caption && (
                  <figcaption className={styles.caption}>
                    {featuredImage?.caption}
                  </figcaption>
                )}
              </figure>
            )}
            <div
              className={clsx(
                { [styles.restrict]: !hasAccess },
                styles.innerstyles,
              )}
            >
              {hasAccess ? markupWithAds : <p>{body}</p>}
            </div>

            {isFeed && (
              <>
                <MPU id={`mpu_bottom-${articleNumber + 1}`} />
                <MobileMPU id={`mobile_mpu_bottom-${articleNumber + 1}`} />
                <div className={styles.commentBlock}>
                  <h3 className={styles.commentHeader}>Discuss This Article</h3>
                  <div className={styles.commentInnerBlock}>
                    <p className={styles.movedText}>Comments have moved.</p>
                    <p className={styles.conversationText}>
                      Join the conversation and talk about this article and all
                      things Kentucky Sports in the new{' '}
                      <Link href="/boards/forums/ksboard.172/">
                        KSR Message Board.
                      </Link>
                    </p>
                    <Link
                      className={styles.boardButton}
                      href="/boards/forums/ksboard.172/"
                    >
                      <Comment />
                      <Text className={styles.innerLink}>KSBoard</Text>
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        {!lastArticle && (
          <a className={styles.seperatorbar} href="https://www.on3.com/">
            <On3HeaderGrey />
          </a>
        )}
      </article>
    </div>
  );
};
